<template>
  <div
    class="base-toggle base-toggle__outer"
    :class="[className, { checked, disabled, error }]"
  >
    <div class="base-toggle__wrapper">
      <slot
        :id="toggleId"
        :checked
        name="label"
        :value="modelValue"
      >
        <label
          v-if="label"
          class="base-toggle__label"
          :for="toggleId"
          @click="simulateBlur"
        >
          {{ label }}
        </label>
      </slot>

      <div
        class="base-toggle__inner"
        :class="{ reverse }"
      >
        <input
          :id="toggleId"
          ref="toggleElement"
          v-model="modelValue"
          class="base-toggle__element base-visually-hidden"
          :disabled
          :name
          :placeholder="placeholder ?? label"
          :type
          :value
          v-bind="attrs"
          @blur="handleBlur"
          @change="handleChange"
          @focus="handleFocus"
          @input="handleInput"
        />

        <slot
          :id="toggleId"
          :checked
          name="prefixTrack"
          :value="modelValue"
        />

        <slot
          v-if="!hideTrack"
          :id="toggleId"
          :checked
          name="track"
          :value="modelValue"
        >
          <label
            class="base-toggle__track"
            :for="toggleId"
            @click="simulateBlur"
          >
            <slot
              :id="toggleId"
              :checked
              name="thumb"
              :value="modelValue"
            >
              <span class="base-toggle__thumb">
                <slot
                  :id="toggleId"
                  :checked
                  name="thumbIcon"
                  :value="modelValue"
                >
                  <BaseIcon2 class="base-toggle__thumb-icon">
                    <path
                      d="M8.4.18 4.15 4.42 1.65 1.6a.65.65 0 0 0-.93-.05.66.66 0 0 0-.05.93l2.94
                        3.3c.13.15.31.23.48.23h.08c.18 0 .34-.06.47-.2L9.3 1.13a.67.67 0 0 0 0-.93.66.66 0 0 0-.93
                        0L8.4.18Z"
                      fill="currentColor"
                    />
                  </BaseIcon2>
                </slot>
              </span>
            </slot>
          </label>
        </slot>

        <div
          v-if="slots.prefix || slots.description || description || slots.suffix"
          class="base-toggle__container"
        >
          <slot
            :id="toggleId"
            :checked
            name="prefix"
            :value="modelValue"
          />

          <slot
            :id="toggleId"
            :checked
            name="description"
            :value="modelValue"
          >
            <label
              v-if="description"
              class="base-toggle__description"
              :for="toggleId"
              @click="simulateBlur"
            >
              {{ description }}
            </label>
          </slot>

          <slot
            :id="toggleId"
            :checked
            name="suffix"
            :value="modelValue"
          />
        </div>
      </div>
    </div>

    <div
      v-if="error || slots.error || hint || slots.hint"
      class="base-toggle__messages"
    >
      <slot
        :id="toggleId"
        :checked
        name="error"
        :value="modelValue"
      >
        <p
          v-if="error"
          class="base-toggle__error"
        >
          {{ error }}
        </p>
      </slot>

      <slot
        :id="toggleId"
        :checked
        name="hint"
        :value="modelValue"
      >
        <p
          v-if="hint"
          class="base-toggle__hint"
        >
          {{ hint }}
        </p>
      </slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { InputHTMLAttributes, TextareaHTMLAttributes } from "vue";

type Type = "checkbox" | "radio";

type Value = (number | string)[] | boolean | null | number | string;

interface Props extends /* @vue-ignore */ Omit<InputHTMLAttributes, "type"> {
  class?: InputHTMLAttributes["class"];
  description?: string;
  disabled?: boolean;
  error?: string;
  hideTrack?: boolean;
  hint?: string;
  id?: TextareaHTMLAttributes["id"];
  label?: string;
  name: InputHTMLAttributes["name"];
  placeholder?: InputHTMLAttributes["placeholder"];
  reverse?: boolean;
  type: Type;
  value?: number | string | boolean;
}

const props = defineProps<Props>();
const modelValue = defineModel<Value>();

const emit = defineEmits<{
  blur: [event: FocusEvent];
  change: [event: Event];
  focus: [event: FocusEvent];
  input: [event: Event];
}>();

defineSlots<{
  description: [checked: boolean, id: InputHTMLAttributes["id"], value: Value];
  error: [checked: boolean, id: InputHTMLAttributes["id"], value: Value];
  hint: [checked: boolean, id: InputHTMLAttributes["id"], value: Value];
  label: [checked: boolean, id: InputHTMLAttributes["id"], value: Value];
  prefix: [checked: boolean, id: InputHTMLAttributes["id"], value: Value];
  prefixTrack: [checked: boolean, id: InputHTMLAttributes["id"], value: Value];
  suffix: [checked: boolean, id: InputHTMLAttributes["id"], value: Value];
  thumb: [checked: boolean, id: InputHTMLAttributes["id"], value: Value];
  thumbIcon: [checked: boolean, id: InputHTMLAttributes["id"], value: Value];
  track: [checked: boolean, id: InputHTMLAttributes["id"], value: Value];
}>();

defineOptions({
  inheritAttrs: false,
});

const attrs = useAttrs();
const slots = useSlots();
const { class: className, id, name, value } = toRefs(props);
const toggleElement = ref<HTMLInputElement | null>(null);
const toggleId = id.value ?? name.value;
const checked = ref(false);

const simulateBlur = () => {
  if (toggleElement.value) {
    toggleElement.value.blur(); // Принудительно вызываем blur
    emit("blur", new FocusEvent("blur", { relatedTarget: toggleElement.value }));
  }
};

const handleBlur = (event: FocusEvent) => {
  emit("blur", event);
};

const handleChange = (event: Event) => {
  emit("change", event);
};

const handleFocus = (event: FocusEvent) => {
  emit("focus", event);
};

const handleInput = (event: Event) => {
  emit("input", event);
};

watch(
  modelValue,
  () => {
    if (Array.isArray(modelValue.value) && (typeof value.value === "string" || typeof value.value === "number")) {
      checked.value = modelValue.value.includes(value.value);

      return;
    }

    if (value.value) {
      checked.value = modelValue.value === value.value;

      return;
    }

    checked.value = Boolean(modelValue.value);
  },
  {
    immediate: true,
  },
);

defineExpose({
  toggleElement,
});
</script>

<style scoped>
.base-toggle__outer {
  display: flex;
  flex-direction: column;
}

.base-toggle__wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.switch-row-separated .base-toggle__wrapper {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.base-toggle__inner {
  display: flex;
  align-items: center;
  width: 100%;
}

.base-toggle__inner.reverse {
  flex-direction: row-reverse;
  justify-content: flex-end;
}

.switch-row-separated .base-toggle__inner {
  justify-content: flex-end;
}

.base-toggle__track {
  position: relative;
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  transition-property: background-color;
  transition-duration: 0.2s;
  transition-timing-function: ease-in-out;
  cursor: pointer;
}

.base-toggle.disabled .base-toggle__track {
  cursor: not-allowed;
}

.base-toggle.disabled {
  position: relative;
  cursor: not-allowed;
}

.base-toggle__thumb {
  position: absolute;
  display: flex;
  transition-property: transform;
  transition-duration: 0.2s;
  transition-timing-function: ease-in-out;
}

.base-toggle__container {
  display: flex;
  flex-grow: 1;
}

.base-toggle__description {
  display: flex;
  cursor: pointer;
}

.base-toggle__label {
  display: flex;
}

.base-toggle.disabled .base-toggle__description,
.base-toggle.disabled .base-toggle__label {
  cursor: default;
  opacity: 0.6;
  color: var(--grey-3);
}

.switch-row-separated .base-toggle__label {
  width: 100%;
}

.base-toggle.disabled .base-toggle__track {
  opacity: 0.5;
  background-color: var(--grey-5) !important;
  border-color: var(--grey-4) !important;
}

.base-toggle.disabled .base-toggle__thumb {
  background-color: var(--grey-4) !important;
}

/* Добавляем визуальный индикатор недоступности */
.base-toggle.disabled::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.05);
  pointer-events: none;
}

.base-toggle__messages {
  display: flex;
  flex-direction: column;
}

.base-toggle__hint {
  color: var(--grey-3);
}
</style>
